/**
 * API service for user registration
 * Uses backend API for user registration
 */

import apiClient from './api.js';

// Register user with Firebase Auth SDK and backend API
export const registerUserWithFirebase = async (userData) => {
  try {
    const { firstName, lastName, email, idToken, referralId, referralRef, referralPrivateCode } = userData;

    // Basic validation
    if (!firstName?.trim() || !lastName?.trim() || !email?.trim() || !idToken) {
      throw new Error('All fields are required');
    }

    // Execute reCAPTCHA for register action
    let recaptchaToken;
    try {
      recaptchaToken = await new Promise((resolve, reject) => {
        grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute('6Lf1JPsrAAAAANMbMPfk9QwitzOdzPwI8CLJUWcP', {action: 'REGISTER'});
          resolve(token);
        });
      });
    } catch (recaptchaError) {
      console.warn('reCAPTCHA failed, proceeding without token:', recaptchaError);
      recaptchaToken = null;
    }

    console.log('🔐 [REGISTER] idToken received:', idToken ? idToken.substring(0, 20) + '...' : 'null');
    console.log('🔐 [REGISTER] idToken length:', idToken ? idToken.length : 0);

    // Prepare data for backend (send ID token and user profile data)
    const registrationData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.toLowerCase().trim(),
      referralId: referralId || null,
      referralRef: referralRef || null,
      referralPrivateCode: referralPrivateCode || null
    };

    console.log('🔐 [REGISTER] Sending registration data:', {
      firstName: registrationData.firstName,
      lastName: registrationData.lastName,
      email: registrationData.email,
      hasReferralId: !!registrationData.referralId,
      hasReferralRef: !!registrationData.referralRef,
      hasReferralPrivateCode: !!registrationData.referralPrivateCode
    });

    // Call backend API with Firebase ID token in Authorization header
    const response = await apiClient.post('/api/auth/register-firebase', registrationData, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'X-Override-Auth': 'true', // Flag to bypass interceptor
        'Content-Type': 'application/json'
      }
    });

    console.log('🔐 [REGISTER] Backend response received');

    console.log('User registered successfully:', response.data.user.email);

    // Return backend response
    return {
      success: true,
      user: response.data.user,
      token: response.data.token,
      message: 'Registration successful'
    };
  } catch (error) {
    console.error('Registration error:', error);

    // Handle different error types
    if (error.response) {
      // Backend returned an error
      const errorMessage = error.response.data.message || 'Registration failed';
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    } else if (error.request) {
      // Network error
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        message: 'Network error. Please check your connection.'
      };
    } else {
      // Other error
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.',
        message: error.message || 'Registration failed. Please try again.'
      };
    }
  }
};

// Note: getRegisteredUsers and findUserByEmail are no longer needed
// as we're using backend authentication

// Validate registration form data
export const validateRegistrationData = (userData) => {
  const { firstName, lastName, email, password, confirmPassword } = userData;
  const errors = {};

  // Required field validation
  if (!firstName?.trim()) errors.firstName = 'First name is required';
  if (!lastName?.trim()) errors.lastName = 'Last name is required';
  if (!email?.trim()) errors.email = 'Email is required';
  if (!password) errors.password = 'Password is required';
  if (!confirmPassword) errors.confirmPassword = 'Please confirm your password';

  // Email format validation
  if (email && !/\S+@\S+\.\S+/.test(email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Password validation
  if (password && password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
  }

  // Password confirmation validation
  if (password && confirmPassword && password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Check if email already exists
export const checkEmailAvailability = async (email) => {
  try {
    // This would typically call a backend endpoint to check email availability
    // For now, we'll assume it's available since we're using in-memory storage
    return { available: true };
  } catch (error) {
    console.error('Email availability check error:', error);
    return { available: true, error: 'Could not verify email availability' };
  }
};

// Get registered user data from Firestore database
export const getRegisteredUserData = async (userId) => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Call backend API to get user data from Firestore
    const response = await apiClient.get(`/api/auth/users/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('User data retrieved from Firestore:', response.data);

    return {
      success: true,
      userData: response.data,
      message: 'User data retrieved successfully from database'
    };
  } catch (error) {
    console.error('Error retrieving user data from database:', error);

    // Handle different error types
    if (error.response) {
      const errorMessage = error.response.data.message || 'Failed to retrieve user data';
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    } else if (error.request) {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        message: 'Network error. Please check your connection.'
      };
    } else {
      return {
        success: false,
        error: error.message || 'Failed to retrieve user data from database.',
        message: error.message || 'Failed to retrieve user data from database.'
      };
    }
  }
};

// Security measures against spam and abuse
export const securityChecks = (userData) => {
  const checks = {
    isValid: true,
    reasons: []
  };

  // Check for suspicious patterns (basic anti-spam)
  const suspiciousPatterns = [
    /test@test\.com/i,
    /admin@/i,
    /root@/i,
    /123456/,
    /password/i,
    /qwerty/i
  ];

  // Check email against suspicious patterns
  if (suspiciousPatterns.some(pattern => pattern.test(userData.email))) {
    checks.isValid = false;
    checks.reasons.push('Suspicious email pattern detected');
  }

  // Check for very short names (potential spam)
  if (userData.firstName?.length < 2 || userData.lastName?.length < 2) {
    checks.isValid = false;
    checks.reasons.push('Name appears to be invalid');
  }

  // Check for common weak passwords
  const weakPasswords = ['123456', 'password', 'qwerty', 'letmein', 'welcome'];
  if (weakPasswords.includes(userData.password)) {
    checks.isValid = false;
    checks.reasons.push('Password is too weak');
  }

  return checks;
};

export default {
  registerUserWithFirebase,
  validateRegistrationData,
  checkEmailAvailability,
  securityChecks,
  getRegisteredUserData
};
